/**
 * @fileoverview Shared TetherSync widget — single source of truth for sync status UI.
 * @module local-db/components/TetherSyncWidget
 *
 * Responsibilities:
 * - Render the TetherSync branded header button with sync-wave animation.
 * - Open a detail panel showing storage health, network, collection sync rows,
 *   push queue status, and conflict alerts.
 * - Host the Sync Activity Log panel with copy-to-clipboard and clear.
 * - Expose optional action slots for Sync Now, Force Resync, and PWA Detail.
 *
 * Dependencies:
 * - getSyncLogEntries, onSyncLogEntry, clearSyncLog: in-package sync activity log store.
 * - lucide-react: icon set.
 *
 * Architecture:
 * - Fully prop-driven — no app-specific hooks, contexts, or API calls.
 * - Each consuming app (tenant-web, system-web, mobile-pwa) provides a thin wrapper
 *   that reads from its own data sources and maps to this component's prop interface.
 * - The Sync Activity Log is self-contained: this component subscribes directly to the
 *   in-package syncActivityLog store, so callers need not thread log state.
 *
 * Notes:
 * - The `animate-tether-waves` Tailwind animation must be defined in the consuming
 *   app's Tailwind config (keyframe already present in all three apps).
 * - Push toasts render only when `onOpenSyncPanel` is provided (mobile-pwa gate).
 *   Desktop apps do not pass this prop, so no floating toasts appear there.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  Shield,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  Database,
  Wifi,
  WifiOff,
  X,
  ArrowDownCircle,
  Upload,
  ChevronDown,
  ChevronRight,
  Copy,
  Trash2,
  FileText,
  RotateCcw,
} from 'lucide-react';
import { getSyncLogEntries, onSyncLogEntry, clearSyncLog } from '../stores/syncActivityLog';
import type { SyncLogEntry } from '../stores/syncActivityLog';
import { useInstallPrompt } from '../storage/useInstallPrompt';
import type { DbStatus } from '../types/dbMeta';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * @contract CollectionSyncRow
 *
 * Purpose:
 * - Normalized sync state for a single Dexie collection, passed from app wrappers.
 *
 * Fields:
 * - name: Dexie collection key (e.g. 'entities').
 * - label: Human-readable display name (e.g. 'Entities'). Pre-computed by wrapper.
 * - status: Current sync state.
 * - lastSyncAt: ISO timestamp of last successful sync.
 * - lastError: Last error message when status === 'error'.
 *
 * Sync:
 * - Provided by app wrapper from its local Dexie context; no server calls.
 */
export interface CollectionSyncRow {
  name: string;
  label: string;
  status: 'active' | 'idle' | 'error' | 'cancelled';
  lastSyncAt?: string | null;
  lastError?: string | null;
}

/**
 * @contract TetherSyncWidgetProps
 *
 * Purpose:
 * - Full prop surface for the shared TetherSync widget.
 * - All app-specific data is mapped by a thin wrapper in each consuming app.
 *
 * Fields:
 * - storagePercent / storageUsed / storageQuota: IndexedDB quota metrics.
 * - isPersisted: Whether storage.persist() has been granted.
 * - lastChecked: ISO timestamp of most recent storage health poll.
 * - onRefreshStorage: Callback to trigger a manual storage health refresh.
 * - online: Current network connectivity.
 * - anyActive: Whether any collection is currently syncing or push is in flight.
 * - collections: Per-collection sync rows (desktop apps); omit for mobile-pwa.
 * - pendingPushCount / failedPushCount / isPushing: Push queue state.
 * - pushJustSucceeded: Brief success state; triggers the green toast (mobile).
 * - unresolvedConflictCount / onOpenConflicts: Conflict resolution integration.
 * - onSyncNow: "Sync Now" button callback; shown whenever online (disabled while syncing).
 * - onForceResync: "Force Full Sync" button callback; clears all sync checkpoints and
 *   triggers a full re-download from the server. Shown only when provided and online.
 *   An inline confirmation step is displayed before executing to prevent accidental use.
 * - onOpenSyncPanel: Opens full SyncMonitorPage (mobile-pwa only; gates toasts).
 * - updateAvailable: Shows PWA update notice (mobile-pwa only).
 * - lastSyncedAt: ISO string of last sync completion for button label (mobile).
 *
 * Rules:
 * - Desktop wrappers omit onOpenSyncPanel; this suppresses mobile-only toasts.
 * - Collections omitted by mobile-pwa; collection rows section is hidden when absent.
 *
 * Sync:
 * - All data is local-first; no API calls originate from this component.
 */
export interface TetherSyncWidgetProps {
  storagePercent: number;
  storageUsed: number;
  storageQuota: number;
  isPersisted: boolean | null;
  lastChecked: string;
  onRefreshStorage: () => void;
  online: boolean;
  anyActive: boolean;
  collections?: CollectionSyncRow[];
  pendingPushCount?: number;
  failedPushCount?: number;
  isPushing?: boolean;
  pushJustSucceeded?: boolean;
  unresolvedConflictCount?: number;
  onOpenConflicts?: () => void;
  onSyncNow?: () => void;
  onForceResync?: () => void;
  onOpenSyncPanel?: () => void;
  updateAvailable?: boolean;
  lastSyncedAt?: string | null;
  /**
   * Local database lifecycle status from the localDatabaseManager. When set to
   * 'updating-app', the widget shows an "Updating application…" banner with a
   * spinner at the top of the detail panel. When 'recovering', shows a
   * "Rebuilding local database…" banner. When 'syncing-pending-writes' (mobile
   * only — see `localDatabaseManager.ts` executeWipeAndRebuild), shows a
   * "Saving your offline changes…" banner so the pre-wipe flush delay isn't
   * perceived as the app hanging. When 'error', shows a red "Database error"
   * banner. 'initializing' is a normal transient state and does not show a
   * banner; 'ready' and null behave the same.
   * See Docs/local-database-manager/README.md → Phase 3.
   */
  dbStatus?: DbStatus | null;
}

type WidgetStatus = 'healthy' | 'warning' | 'critical' | 'update';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function timeAgo(iso: string | null | undefined): string {
  if (!iso) return 'never';
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 60_000) return 'just now';
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  return `${Math.floor(diff / 3_600_000)}h ago`;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatLogsAsText(entries: SyncLogEntry[]): string {
  return [...entries]
    .reverse()
    .map(
      (e) =>
        `[${new Date(e.timestamp).toLocaleTimeString()}] [${e.level.toUpperCase()}] ${e.message}` +
        (e.collection ? ` (${e.collection})` : '') +
        (e.detail ? ` — ${e.detail}` : ''),
    )
    .join('\n');
}

const LEVEL_TEXT: Record<SyncLogEntry['level'], string> = {
  info: 'text-blue-400',
  warn: 'text-amber-400',
  error: 'text-red-400',
  success: 'text-emerald-400',
};

const LEVEL_ROW: Record<SyncLogEntry['level'], string> = {
  info: 'border-l-blue-600/50',
  warn: 'border-l-amber-600/50',
  error: 'border-l-red-600/60',
  success: 'border-l-emerald-600/50',
};

// ---------------------------------------------------------------------------
// Sub-component: Sync Activity Log Panel
// ---------------------------------------------------------------------------

/**
 * @component SyncActivityLogPanel
 *
 * Purpose:
 * - Collapsible log panel embedded in TetherSync detail panel.
 * - Self-subscribes to the in-package syncActivityLog store.
 *
 * Behavior:
 * - Starts collapsed; expands on click of header row.
 * - Shows up to 100 most recent entries (ring buffer holds 500).
 * - Copy button writes formatted plain-text log to clipboard.
 * - Clear button empties the in-memory ring buffer.
 *
 * Data Source:
 * - local (in-package syncActivityLog store — no API calls).
 *
 * Constraints:
 * - MUST NOT call any API endpoints directly.
 *
 * Performance:
 * - Subscribes via onSyncLogEntry (single-entry callback) to avoid full list
 *   re-renders on every new entry.
 */
function SyncActivityLogPanel() {
  const [entries, setEntries] = useState<SyncLogEntry[]>(() => getSyncLogEntries());
  const [expanded, setExpanded] = useState(false);
  const [copyFlash, setCopyFlash] = useState(false);
  const copyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return onSyncLogEntry((entry) => {
      setEntries((prev) => [entry, ...prev].slice(0, 500));
    });
  }, []);

  useEffect(() => {
    return () => {
      if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
    };
  }, []);

  const handleCopy = useCallback(() => {
    const text = formatLogsAsText(entries);
    void navigator.clipboard.writeText(text).then(() => {
      setCopyFlash(true);
      copyTimerRef.current = setTimeout(() => setCopyFlash(false), 1500);
    });
  }, [entries]);

  const handleClear = useCallback(() => {
    clearSyncLog();
    setEntries([]);
  }, []);

  return (
    <div className="rounded-lg overflow-hidden border" style={{ borderColor: 'var(--tp-panel-border)' }}>
      {/* Header — div not button to avoid nesting <button> inside <button> */}
      <div
        role="button"
        tabIndex={0}
        className="w-full flex items-center justify-between px-2.5 py-1.5 transition-colors cursor-pointer"
        style={{ background: 'var(--tp-panel-surface)' }}
        onClick={() => setExpanded((v) => !v)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setExpanded((v) => !v); }}
      >
        <div className="flex items-center gap-1.5">
          <FileText className="h-3 w-3" style={{ color: 'var(--tp-panel-text-muted)' }} />
          <span className="text-[10px] uppercase tracking-wide font-medium" style={{ color: 'var(--tp-panel-text-muted)' }}>
            Sync Log
          </span>
          {entries.length > 0 && (
            <span className="text-[9px] px-1.5 rounded-full leading-4" style={{ color: 'var(--tp-panel-text-muted)', background: 'var(--tp-panel-surface)' }}>
              {entries.length}
            </span>
          )}
        </div>
        <div className="flex items-center gap-0.5">
          {entries.length > 0 && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleCopy();
                }}
                className={`p-1 rounded-sm transition-colors ${copyFlash ? 'text-emerald-400' : ''}`}
                style={!copyFlash ? { color: 'var(--tp-panel-text-muted)' } : undefined}
                title="Copy log to clipboard"
              >
                <Copy className="h-3 w-3" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleClear();
                }}
                className="p-1 rounded-sm hover:text-red-400 transition-colors"
                style={{ color: 'var(--tp-panel-text-muted)' }}
                title="Clear log"
              >
                <Trash2 className="h-3 w-3" />
              </button>
            </>
          )}
          {expanded ? (
            <ChevronDown className="h-3 w-3" style={{ color: 'var(--tp-panel-text-muted)' }} />
          ) : (
            <ChevronRight className="h-3 w-3" style={{ color: 'var(--tp-panel-text-muted)' }} />
          )}
        </div>
      </div>

      {/* Log entries */}
      {expanded && (
        <div className="max-h-40 overflow-y-auto divide-y divide-white/4">
          {entries.length === 0 ? (
            <p className="px-2.5 py-2 text-[10px] italic" style={{ color: 'var(--tp-panel-text-muted)' }}>No activity recorded</p>
          ) : (
            entries.slice(0, 100).map((entry) => (
              <div
                key={entry.id}
                className={`flex gap-2 px-2.5 py-1.5 border-l-2 ${LEVEL_ROW[entry.level]}`}
              >
                <span className="shrink-0 text-[9px] font-mono mt-0.5 tabular-nums" style={{ color: 'var(--tp-panel-text-muted)' }}>
                  {new Date(entry.timestamp).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit',
                  })}
                </span>
                <div className="min-w-0">
                  <span className={`text-[10px] font-semibold mr-1 ${LEVEL_TEXT[entry.level]}`}>
                    [{entry.level.toUpperCase()}]
                  </span>
                  <span className="text-[10px]" style={{ color: 'var(--tp-panel-text)' }}>{entry.message}</span>
                  {entry.collection && (
                    <span className="ml-1 text-[9px] px-1 rounded-sm" style={{ background: 'var(--tp-panel-surface)', color: 'var(--tp-panel-text-muted)' }}>
                      {entry.collection}
                    </span>
                  )}
                  {entry.detail && (
                    <p className="text-[9px] mt-0.5 break-all" style={{ color: 'var(--tp-panel-text-muted)' }}>{entry.detail}</p>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

/**
 * @component TetherSyncWidget
 *
 * Purpose:
 * - Shared TetherSync header widget consumed by tenant-web, system-web, and mobile-pwa.
 * - Single source of truth for all sync status UI across the application suite.
 *
 * Props:
 * - See TetherSyncWidgetProps for full interface documentation.
 *
 * Behavior:
 * - Button displays the TetherSync shield + animated dot + sync label.
 * - `animate-tether-waves` CSS class is applied while anyActive is true.
 * - Clicking the button opens/closes the detail panel.
 * - Clicking outside the panel closes it.
 * - Collection rows, SSE stream indicator, push banner, and action buttons
 *   are rendered conditionally based on provided props.
 * - Activity Log sub-component is always rendered within the panel.
 * - Push toasts (success / failed) are gated by presence of onOpenSyncPanel prop
 *   so they only appear in the mobile-pwa context.
 *
 * Data Source:
 * - local (all data provided as props from the calling app's thin wrapper).
 *
 * Constraints:
 * - MUST NOT call any API endpoints directly.
 * - MUST NOT import any app-specific hooks, contexts, or services.
 *
 * Performance:
 * - SyncActivityLogPanel manages its own subscription; this component does not
 *   re-render on every log entry.
 * - syncDebounceRef prevents "Active" badge flash for short-lived sync transitions.
 */
export function TetherSyncWidget({
  storagePercent,
  storageUsed,
  storageQuota,
  isPersisted,
  lastChecked,
  onRefreshStorage,
  online,
  anyActive,
  collections,
  pendingPushCount = 0,
  failedPushCount = 0,
  isPushing = false,
  pushJustSucceeded = false,
  unresolvedConflictCount = 0,
  onOpenConflicts,
  onSyncNow,
  onForceResync,
  onOpenSyncPanel,
  updateAvailable = false,
  lastSyncedAt,
  dbStatus = null,
}: TetherSyncWidgetProps) {
  const [open, setOpen] = useState(false);
  const [syncingVisible, setSyncingVisible] = useState(false);
  const [confirmForceResync, setConfirmForceResync] = useState(false);
  const syncDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  // Fixed-position coordinates for the detail panel. The panel is rendered
  // through a portal to document.body (see below) so it cannot be trapped
  // behind an app header's stacking context / backdrop-blur-sm (which previously
  // hid it behind open detail slide-outs). Coordinates are derived from the
  // trigger button's viewport rect and recomputed while the panel is open.
  const [panelPos, setPanelPos] = useState<{ top: number; right: number } | null>(null);

  // ── Debounced syncingVisible prevents "Active" badge flash ────────────────
  useEffect(() => {
    if (anyActive) {
      if (!syncDebounceRef.current) {
        syncDebounceRef.current = setTimeout(() => setSyncingVisible(true), 3000);
      }
    } else {
      if (syncDebounceRef.current) {
        clearTimeout(syncDebounceRef.current);
        syncDebounceRef.current = null;
      }
      setSyncingVisible(false);
    }
    return () => {};
  }, [anyActive]);

  // ── Reset confirmation state whenever the panel closes ───────────────────
  useEffect(() => {
    if (!open) setConfirmForceResync(false);
  }, [open]);

  // ── Close panel on outside click ──────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (
        panelRef.current &&
        !panelRef.current.contains(e.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // ── Position the portal panel under the trigger button ────────────────────
  // The panel is portalled to document.body to escape the header stacking
  // context, so it must be positioned manually from the button's viewport
  // rect. Recompute on open and on resize/scroll so it stays anchored.
  useEffect(() => {
    if (!open) return;
    const reposition = () => {
      const btn = buttonRef.current;
      if (!btn) return;
      const rect = btn.getBoundingClientRect();
      setPanelPos({
        top: rect.bottom + 8, // mirrors the previous `mt-2` (8px) gap
        right: Math.max(window.innerWidth - rect.right, 8),
      });
    };
    reposition();
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);
    return () => {
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
    };
  }, [open]);

  // ── Derived values ────────────────────────────────────────────────────────
  const errorCount = (collections ?? []).filter((c) => c.status === 'error').length;

  const status: WidgetStatus =
    storagePercent >= 90 ||
    errorCount > 0 ||
    unresolvedConflictCount > 0 ||
    failedPushCount > 0
      ? 'critical'
      : updateAvailable
        ? 'update'
        : storagePercent >= 70 || isPersisted === false
          ? 'warning'
          : 'healthy';

  // ── Status visual config ──────────────────────────────────────────────────
  const statusConfig = {
    healthy: {
      shieldColor: '#10b981',
      dotColor: 'bg-emerald-400',
      pulseColor: 'bg-emerald-400',
      label: 'TetherSync',
      labelColor: 'text-emerald-400',
      flashClass: '',
    },
    warning: {
      shieldColor: '#f59e0b',
      dotColor: 'bg-amber-400',
      pulseColor: 'bg-amber-400',
      label: 'TetherSync',
      labelColor: 'text-amber-400',
      flashClass: '',
    },
    critical: {
      shieldColor: '#ef4444',
      dotColor: 'bg-red-500',
      pulseColor: 'bg-red-500',
      label: 'Alert',
      labelColor: 'text-red-400',
      flashClass: 'animate-pulse',
    },
    update: {
      shieldColor: '#3b82f6',
      dotColor: 'bg-blue-400',
      pulseColor: 'bg-blue-400',
      label: 'Update',
      labelColor: 'text-blue-400',
      flashClass: 'animate-pulse',
    },
  } as const;

  const cfg = statusConfig[status];

  // ── Button secondary label ────────────────────────────────────────────────
  const syncLabel = !online
    ? 'Offline'
    : isPushing
      ? pendingPushCount > 0
        ? `Pushing ${pendingPushCount}…`
        : 'Pushing…'
      : failedPushCount > 0
        ? `${failedPushCount} push failed`
        : pushJustSucceeded
          ? 'Pushed ✓'
          : anyActive
            ? 'Syncing…'
            : errorCount > 0
              ? `${errorCount} error${errorCount !== 1 ? 's' : ''}`
              : unresolvedConflictCount > 0
                ? `${unresolvedConflictCount} conflict${unresolvedConflictCount !== 1 ? 's' : ''}`
                : pendingPushCount > 0
                  ? `${pendingPushCount} pending`
                  : lastSyncedAt
                    ? `Synced ${timeAgo(lastSyncedAt)}`
                    : 'Synced';

  const syncLabelColor =
    !online || errorCount > 0 || failedPushCount > 0 || unresolvedConflictCount > 0
      ? 'text-red-400'
      : isPushing || anyActive || pushJustSucceeded
        ? 'text-emerald-400'
        : pendingPushCount > 0
          ? 'text-amber-400'
          : 'text-white/40';

  const showCollections = collections !== undefined;
  const hasPushBanner =
    (pendingPushCount > 0 || failedPushCount > 0 || isPushing) && !pushJustSucceeded;
  const isMobile = onOpenSyncPanel !== undefined;

  // ── Install-prompt capture ────────────────────────────────────────────────
  // When storage persistence is in best-effort mode, installing the PWA causes
  // Chrome to auto-grant navigator.storage.persist() on the next poll cycle.
  // We expose an "Install app" affordance only when:
  //   (1) the browser has fired beforeinstallprompt (canInstall === true), AND
  //   (2) storage persistence is currently false (offering it for already-
  //       persisted origins would just be noise).
  const { canInstall: canInstallApp, promptInstall } = useInstallPrompt();
  const showInstallPrompt = canInstallApp && isPersisted === false;

  return (
    <div className="relative flex items-center">
      {/* ── Trigger button ───────────────────────────────────────────────── */}
      <button
        ref={buttonRef}
        onClick={() => setOpen((v) => !v)}
        className={`
          relative flex flex-col items-center justify-center gap-0.5
          px-2.5 py-1 rounded-lg transition-colors duration-200
          bg-slate-800 hover:bg-slate-700
          border border-slate-600 shadow-xs
          ${anyActive ? 'animate-tether-waves bg-[linear-gradient(90deg,#1e293b_0%,#1e293b_25%,rgba(16,185,129,0.35)_45%,#1e293b_65%,#1e293b_100%)] bg-size-[220%_100%] bg-no-repeat' : ''}
          ${cfg.flashClass}
        `}
        title="TetherSync — local-first storage health"
      >
        <span className="relative">
          <Shield
            className="h-5 w-5 transition-colors duration-300"
            style={{ color: cfg.shieldColor }}
            strokeWidth={2}
          />
          <span
            className={`absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full ${cfg.dotColor} ring-1 ring-black/20`}
          >
            {status !== 'critical' && (
              <span
                className={`absolute inset-0 rounded-full ${cfg.pulseColor} animate-ping opacity-60`}
              />
            )}
          </span>
        </span>
        <span
          className={`text-[9px] font-bold tracking-widest leading-none ${cfg.labelColor}`}
        >
          {cfg.label}
        </span>
        <span
          className={`text-[9px] font-bold italic leading-none ${syncLabelColor}`}
          style={{ letterSpacing: '0.02em' }}
        >
          {syncLabel}
        </span>
        {(pendingPushCount > 0 || failedPushCount > 0) && !pushJustSucceeded && (
          <span
            className={`absolute -top-1.5 -right-1.5 flex items-center justify-center min-w-[16px] h-4 px-1 text-[9px] font-bold text-white rounded-full ${failedPushCount > 0 ? 'bg-red-500' : 'bg-amber-500'}`}
          >
            {failedPushCount || pendingPushCount}
          </span>
        )}
      </button>

      {/* ── Expanded detail panel ─────────────────────────────────────────── */}
      {/* Portalled to <body> so it renders above app detail slide-outs and is
          not clipped/trapped by the header's stacking context or backdrop
          blur. Positioned manually from the trigger button's viewport rect. */}
      {open && panelPos && createPortal(
        <div
          ref={panelRef}
          className="fixed w-80 z-150 rounded-xl shadow-2xl border overflow-hidden"
          style={{
            top: panelPos.top,
            right: panelPos.right,
            maxHeight: `calc(100vh - ${panelPos.top + 16}px)`,
            overflowY: 'auto',
            background: 'var(--tp-panel-bg)',
            color: 'var(--tp-panel-text)',
            borderColor: 'var(--tp-panel-border)',
          }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: 'var(--tp-panel-divide)' }}>
            <div className="flex items-center gap-2">
              <Shield className="h-4 w-4" style={{ color: cfg.shieldColor }} />
              <span className="text-sm font-bold tracking-wide" style={{ color: 'var(--tp-panel-text)' }}>TetherSync</span>
              {status === 'critical' && (
                <span className="text-[10px] font-bold uppercase tracking-widest text-red-400 bg-red-950 px-1.5 py-0.5 rounded-full animate-pulse">
                  Alert
                </span>
              )}
              {status === 'update' && (
                <span className="text-[10px] font-bold uppercase tracking-widest text-blue-400 bg-blue-950 px-1.5 py-0.5 rounded-full animate-pulse">
                  Update
                </span>
              )}
            </div>
            <button
              onClick={() => setOpen(false)}
              className="transition-opacity hover:opacity-100 opacity-50"
              style={{ color: 'var(--tp-panel-text-muted)' }}
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>

          {/* Tagline */}
          <div className="px-4 py-2 border-b" style={{ background: 'var(--tp-panel-surface)', borderColor: 'var(--tp-panel-divide)' }}>
            <p className="text-[10px] tracking-wide uppercase font-medium" style={{ color: 'var(--tp-panel-text-muted)' }}>
              Local-first · Always available · Zero data loss
            </p>
          </div>

          {/* ── Database lifecycle status banner ─────────────────────────── */}
          {/* Shows when the localDatabaseManager reports a non-ready status
              that the user needs to be aware of (updating-app, recovering). */}
          {dbStatus === 'updating-app' && (
            <div className="flex items-center gap-2.5 px-4 py-3 border-b bg-blue-950/60" style={{ borderColor: 'var(--tp-panel-divide)' }}>
              <RefreshCw className="h-4 w-4 text-blue-400 shrink-0 animate-spin" />
              <div>
                <p className="text-xs font-semibold text-blue-300">Updating application…</p>
                <p className="text-[10px] text-blue-400/70 mt-0.5">
                  A newer version is required. The page will reload automatically.
                </p>
              </div>
            </div>
          )}
          {dbStatus === 'recovering' && (
            <div className="flex items-center gap-2.5 px-4 py-3 border-b bg-amber-950/60" style={{ borderColor: 'var(--tp-panel-divide)' }}>
              <Database className="h-4 w-4 text-amber-400 shrink-0 animate-pulse" />
              <div>
                <p className="text-xs font-semibold text-amber-300">Rebuilding local database…</p>
                <p className="text-[10px] text-amber-400/70 mt-0.5">
                  Your data will re-sync from the server. This may take a moment.
                </p>
              </div>
            </div>
          )}
          {dbStatus === 'syncing-pending-writes' && (
            <div className="flex items-center gap-2.5 px-4 py-3 border-b bg-amber-950/60" style={{ borderColor: 'var(--tp-panel-divide)' }}>
              <RefreshCw className="h-4 w-4 text-amber-400 shrink-0 animate-spin" />
              <div>
                <p className="text-xs font-semibold text-amber-300">Saving your offline changes…</p>
                <p className="text-[10px] text-amber-400/70 mt-0.5">
                  Please stay on this screen while unsynced changes are sent to the server.
                </p>
              </div>
            </div>
          )}
          {dbStatus === 'error' && (
            <div className="flex items-center gap-2.5 px-4 py-3 border-b bg-red-950/60" style={{ borderColor: 'var(--tp-panel-divide)' }}>
              <AlertTriangle className="h-4 w-4 text-red-400 shrink-0" />
              <div>
                <p className="text-xs font-semibold text-red-300">Database error</p>
                <p className="text-[10px] text-red-400/70 mt-0.5">
                  Local database recovery failed. Please refresh the page or contact support.
                </p>
              </div>
            </div>
          )}

          <div className="px-4 py-3 space-y-3 overflow-y-auto max-h-[70vh]">
            {/* ── Connectivity grid ── */}
            <div className="grid grid-cols-2 gap-2">
              {/* Network */}
              <div
                className={`flex items-center gap-1.5 rounded-lg px-2.5 py-2 ${online ? 'bg-emerald-950/60 border border-emerald-800/40' : 'bg-red-950/60 border border-red-800/40'}`}
              >
                {online ? (
                  <Wifi className="h-3.5 w-3.5 text-emerald-400 shrink-0" />
                ) : (
                  <WifiOff className="h-3.5 w-3.5 text-red-400 shrink-0" />
                )}
                <div>
                  <p className="text-[10px] uppercase tracking-wide" style={{ color: 'var(--tp-panel-text-muted)' }}>Network</p>
                  <p
                    className={`text-xs font-semibold ${online ? 'text-emerald-300' : 'text-red-300'}`}
                  >
                    {online ? 'Online' : 'Offline'}
                  </p>
                </div>
              </div>

              {/* Persistence */}
              <div
                className={`flex items-center gap-1.5 rounded-lg px-2.5 py-2 border ${
                  isPersisted === true
                    ? 'bg-emerald-950/60 border-emerald-800/40'
                    : isPersisted === false
                      ? 'bg-amber-950/60 border-amber-800/40'
                      : ''
                }`}
                style={isPersisted === null ? { background: 'var(--tp-panel-surface)', borderColor: 'var(--tp-panel-border)' } : undefined}
              >
                <Database
                  className={`h-3.5 w-3.5 shrink-0 ${
                    isPersisted === true
                      ? 'text-emerald-400'
                      : isPersisted === false
                        ? 'text-amber-400'
                        : ''
                  }`}
                  style={isPersisted === null ? { color: 'var(--tp-panel-text-muted)' } : undefined}
                />
                <div>
                  <p className="text-[10px] uppercase tracking-wide" style={{ color: 'var(--tp-panel-text-muted)' }}>Storage</p>
                  <p
                    className={`text-xs font-semibold ${
                      isPersisted === true
                        ? 'text-emerald-300'
                        : isPersisted === false
                          ? 'text-amber-300'
                          : ''
                    }`}
                    style={isPersisted === null ? { color: 'var(--tp-panel-text-muted)' } : undefined}
                  >
                    {isPersisted === null ? '…' : isPersisted ? 'Protected' : 'Best-effort'}
                  </p>
                </div>
              </div>
            </div>

            {/* ── Best-effort storage banner — shows when storage is not persisted ── */}
            {isPersisted === false && (
              <div className="rounded-lg bg-amber-950/40 border border-amber-800/30 px-3 py-2 space-y-1.5">
                <div className="flex items-center gap-2">
                  <Database className="h-3.5 w-3.5 text-amber-400 shrink-0" />
                  <p className="text-xs font-semibold text-amber-300">Best-effort storage</p>
                </div>
                <p className="text-[10px] leading-relaxed text-amber-200/70">
                  Your browser may evict this app's local data under disk
                  pressure. {showInstallPrompt
                    ? 'Install the app to upgrade to protected storage.'
                    : 'Use the app regularly or install it from your browser menu to upgrade to protected storage.'}
                </p>
                {showInstallPrompt && (
                  <button
                    onClick={() => {
                      void promptInstall();
                    }}
                    className="w-full mt-1 py-1.5 rounded-md bg-amber-700/60 border border-amber-600/40 text-[11px] font-semibold text-amber-100 hover:bg-amber-600/60 transition-colors flex items-center justify-center gap-1.5"
                  >
                    <ArrowDownCircle className="h-3 w-3" />
                    Install app
                  </button>
                )}
              </div>
            )}

            {/* ── Push queue banner ── */}
            {hasPushBanner && (
              <div
                className={`flex items-center gap-2 rounded-lg px-3 py-2 border ${
                  failedPushCount > 0
                    ? 'bg-red-950/60 border-red-800/40'
                    : 'bg-amber-950/40 border-amber-800/30'
                }`}
              >
                {isPushing ? (
                  <Upload className="h-3.5 w-3.5 text-amber-400 animate-pulse shrink-0" />
                ) : failedPushCount > 0 ? (
                  <AlertTriangle className="h-3.5 w-3.5 text-red-400 shrink-0" />
                ) : (
                  <Upload className="h-3.5 w-3.5 text-amber-400 shrink-0" />
                )}
                <div>
                  <p className="text-[10px] uppercase tracking-wide" style={{ color: 'var(--tp-panel-text-muted)' }}>Push Queue</p>
                  <p
                    className={`text-xs font-semibold ${failedPushCount > 0 ? 'text-red-300' : 'text-amber-300'}`}
                  >
                    {failedPushCount > 0
                      ? `${failedPushCount} item${failedPushCount !== 1 ? 's' : ''} failed`
                      : isPushing
                        ? `Pushing ${pendingPushCount} change${pendingPushCount !== 1 ? 's' : ''}…`
                        : `${pendingPushCount} pending`}
                  </p>
                </div>
              </div>
            )}

            {/* ── Conflicts banner ── */}
            {unresolvedConflictCount > 0 && (
              <button
                onClick={() => {
                  setOpen(false);
                  onOpenConflicts?.();
                }}
                className="w-full flex items-center gap-2 rounded-lg px-3 py-2 bg-red-950/60 border border-red-800/40 hover:bg-red-900/60 transition-colors text-left"
              >
                <AlertTriangle className="h-3.5 w-3.5 text-red-400 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-red-300">
                    {unresolvedConflictCount} sync conflict
                    {unresolvedConflictCount > 1 ? 's' : ''} detected
                  </p>
                  <p className="text-[10px] text-red-400/70">Click to review and resolve →</p>
                </div>
              </button>
            )}

            {/* ── Storage usage bar ── */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[10px] uppercase tracking-wide font-medium" style={{ color: 'var(--tp-panel-text-muted)' }}>
                  Local Storage
                </span>
                <span className="text-[10px] font-mono" style={{ color: 'var(--tp-panel-text-muted)' }}>
                  {formatBytes(storageUsed)} / {formatBytes(storageQuota)}
                </span>
              </div>
              <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--tp-panel-surface)' }}>
                <div
                  className={`h-full rounded-full transition-all duration-700 ${
                    storagePercent >= 90
                      ? 'bg-red-500'
                      : storagePercent >= 70
                        ? 'bg-amber-400'
                        : 'bg-emerald-400'
                  }`}
                  style={{ width: `${Math.min(storagePercent, 100)}%` }}
                />
              </div>
              <p className="mt-1 text-[10px] text-right" style={{ color: 'var(--tp-panel-text-muted)' }}>
                {storagePercent.toFixed(1)}% used
              </p>
            </div>

            {/* ── Collection sync rows — desktop apps only ── */}
            {showCollections && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] uppercase tracking-wide font-medium" style={{ color: 'var(--tp-panel-text-muted)' }}>
                    Background Sync
                  </span>
                  {syncingVisible && errorCount === 0 && (
                    <span className="flex items-center gap-1 text-[10px] text-emerald-400">
                      <RefreshCw className="h-2.5 w-2.5 animate-spin" />
                      Active
                    </span>
                  )}
                  {!syncingVisible && errorCount === 0 && (
                    <span className="flex items-center gap-1 text-[10px] text-emerald-400">
                      <CheckCircle className="h-2.5 w-2.5" />
                      All current
                    </span>
                  )}
                  {errorCount > 0 && (
                    <span className="flex items-center gap-1 text-[10px] text-red-400 animate-pulse">
                      <AlertTriangle className="h-2.5 w-2.5" />
                      {errorCount} error{errorCount > 1 ? 's' : ''}
                    </span>
                  )}
                </div>

                {collections!.length === 0 ? (
                  <div className="flex items-center gap-2 rounded-lg px-3 py-2 border" style={{ background: 'var(--tp-panel-surface)', borderColor: 'var(--tp-panel-border)' }}>
                    <Shield className="h-3.5 w-3.5 shrink-0" style={{ color: 'var(--tp-panel-text-muted)' }} />
                    <p className="text-xs" style={{ color: 'var(--tp-panel-text-muted)' }}>
                      Sync inactive — data lives safely in local storage
                    </p>
                  </div>
                ) : (
                  <div className="space-y-1 max-h-40 overflow-y-auto pr-1">
                    {collections!.map((col) => (
                      <div
                        key={col.name}
                        className={`rounded-md px-2.5 py-1.5 text-xs transition-colors border ${
                          col.status === 'error'
                            ? 'bg-red-950/50 border-red-800/40'
                            : col.status === 'active'
                              ? 'bg-emerald-950/40 border-emerald-800/30'
                              : ''
                        }`}
                        style={(col.status === 'idle' || col.status === 'cancelled') ? { background: 'var(--tp-panel-surface)', borderColor: 'var(--tp-panel-border)' } : undefined}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-medium" style={{ color: 'var(--tp-panel-text)' }}>{col.label}</span>
                          <span
                            className={`flex items-center gap-1 text-[10px] ${
                              col.status === 'error'
                                ? 'text-red-400'
                                : col.status === 'active'
                                  ? 'text-emerald-400'
                                  : ''
                            }`}
                            style={(col.status === 'idle' || col.status === 'cancelled') ? { color: 'var(--tp-panel-text-muted)' } : undefined}
                          >
                            {col.status === 'error' && (
                              <AlertTriangle className="h-2.5 w-2.5" />
                            )}
                            {col.status === 'active' && (
                              <RefreshCw className="h-2.5 w-2.5 animate-spin" />
                            )}
                            {(col.status === 'idle' || col.status === 'cancelled') && (
                              <span className="h-1 w-1 rounded-full inline-block" style={{ background: 'var(--tp-panel-text-muted)' }} />
                            )}
                            {col.status === 'error'
                              ? 'Error'
                              : col.status === 'active'
                                ? 'Syncing'
                                : col.lastSyncAt
                                  ? timeAgo(col.lastSyncAt)
                                  : 'Idle'}
                          </span>
                        </div>
                        {col.status === 'error' && col.lastError && (
                          <p className="mt-0.5 text-[9px] text-red-400/70 break-all">
                            {col.lastError}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ── Sync Activity Log ── */}
            <SyncActivityLogPanel />

            {/* ── PWA update notice — mobile only ── */}
            {updateAvailable && (
              <div className="rounded-lg bg-blue-950/60 border border-blue-700/40 px-3 py-2.5 space-y-1.5">
                <div className="flex items-center gap-2">
                  <ArrowDownCircle className="h-3.5 w-3.5 text-blue-400 shrink-0" />
                  <p className="text-xs font-semibold text-blue-300">App update available</p>
                </div>
                <p className="text-[10px] text-blue-200/70 leading-relaxed">
                  A new version of TetherSync is ready. Close and reopen the app to apply.
                </p>
              </div>
            )}

            {/* ── Sync Now — always shown when online and callback provided; disabled while syncing ── */}
            {onSyncNow && online && (
              <button
                onClick={() => {
                  if (!anyActive) {
                    onSyncNow();
                    setOpen(false);
                  }
                }}
                disabled={anyActive}
                className="w-full py-1.5 rounded-lg bg-emerald-900/50 border border-emerald-700/40 text-[11px] font-semibold text-emerald-300 hover:bg-emerald-800/50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-1.5"
              >
                <RefreshCw className="h-3 w-3" />
                Sync Now
              </button>
            )}

            {/* ── Force Full Sync — clears all checkpoints and re-downloads all data ── */}
            {onForceResync && online && (
              confirmForceResync ? (
                <div className="rounded-lg bg-amber-950/40 border border-amber-800/30 px-3 py-3 space-y-2.5">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="h-3.5 w-3.5 text-amber-400 shrink-0 mt-0.5" />
                    <p className="text-[11px] font-semibold text-amber-300 leading-relaxed">
                      This will clear all sync checkpoints and re-download all data from the server. Unsynced local changes in your push queue will be preserved.
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setOpen(false);
                        onForceResync();
                      }}
                      disabled={anyActive}
                      className="flex-1 py-1.5 rounded-md bg-amber-700/60 border border-amber-600/40 text-[11px] font-semibold text-amber-100 hover:bg-amber-600/60 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      Confirm
                    </button>
                    <button
                      onClick={() => setConfirmForceResync(false)}
                      className="flex-1 py-1.5 rounded-md text-[11px] font-semibold transition-colors hover:opacity-80"
                      style={{ background: 'var(--tp-panel-surface)', borderColor: 'var(--tp-panel-border)', color: 'var(--tp-panel-text-muted)', border: '1px solid var(--tp-panel-border)' }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setConfirmForceResync(true)}
                  disabled={anyActive}
                  className="w-full py-1.5 rounded-lg border text-[11px] font-semibold transition-colors hover:opacity-80 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-1.5"
                  style={{ background: 'var(--tp-panel-surface)', borderColor: 'var(--tp-panel-border)', color: 'var(--tp-panel-text-muted)' }}
                  title="Clear all local sync checkpoints and re-download all data from the server"
                >
                  <RotateCcw className="h-3 w-3" />
                  Force Full Sync
                </button>
              )
            )}
          </div>

          {/* Footer */}
          <div className="px-4 py-2.5 border-t flex items-center justify-between" style={{ borderColor: 'var(--tp-panel-divide)', background: 'var(--tp-panel-surface)' }}>
            <p className="text-[10px]" style={{ color: 'var(--tp-panel-text-muted)' }}>Checked {timeAgo(lastChecked)}</p>
            <button
              onClick={() => void onRefreshStorage()}
              className="flex items-center gap-1 text-[10px] transition-opacity hover:opacity-100 opacity-50"
              style={{ color: 'var(--tp-panel-text-muted)' }}
            >
              <RefreshCw className="h-2.5 w-2.5" />
              Refresh
            </button>
          </div>
        </div>,
        document.body,
      )}

      {/* ── Push success toast — mobile only (gated by isMobile) ─────────── */}
      {isMobile && pushJustSucceeded && (
        <div
          className="fixed left-1/2 -translate-x-1/2 z-200 flex items-center gap-2 rounded-full bg-emerald-600 px-4 py-2 shadow-lg animate-slide-up"
          style={{ bottom: 80 }}
        >
          <CheckCircle className="h-4 w-4 text-white" />
          <span className="text-sm font-semibold text-white">Changes saved to server</span>
        </div>
      )}

      {/* ── Push failed toast — mobile only (gated by isMobile) ──────────── */}
      {isMobile && failedPushCount > 0 && !isPushing && !pushJustSucceeded && (
        <div
          className="fixed left-1/2 -translate-x-1/2 z-200 flex items-center gap-2 rounded-full bg-red-600 px-4 py-2 shadow-lg animate-pulse"
          style={{ bottom: 80 }}
        >
          <AlertTriangle className="h-4 w-4 text-white" />
          <span className="text-sm font-semibold text-white">Push failed — tap sync to retry</span>
        </div>
      )}
    </div>
  );
}
